import type { PropsWithChildren } from "react";
import { useMemo, useState } from "react";

import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../app/navigation";
import { useMonitorAndProtectCampaigns } from "../../services/api/campaign";
import { useGetTrademarks } from "../../services/api/trademarks";
import { TrademarkPermissions } from "../../services/asset-permissions";
import { trademarksTracking } from "../../services/tracking/trademarks";
import { ErrorCampaignState, PageLoadingState } from "../../shared/components/page-states";
import { useSelectTrademark } from "../../shared/hooks/use-select-trademark";
import { getNoData } from "../../shared/ui/table/get-no-data";

import { TrademarkDashboardEmpty } from "./empty-configs/trademark-violations-empty";
import { getTableColumnConfig } from "./table-config";
import type { TrademarkTableRow } from "./table-config";

const ContentSwitcher = ({ children }: PropsWithChildren) => {
    const { isLoading, data: trademarkList = [], isError } = useGetTrademarks();
    const {
        isLoading: isLoadingCampaigns,
        data: campaignList = [],
        isError: isErrorCampaigns,
    } = useMonitorAndProtectCampaigns();

    if (isLoading || isLoadingCampaigns) {
        return <PageLoadingState />;
    }

    if (isError || isErrorCampaigns) {
        return <ErrorCampaignState />;
    }

    if (trademarkList.length === 0 || campaignList.length === 0) {
        return <TrademarkDashboardEmpty />;
    }

    return children;
};

export const TrademarkConfigsPage = () => {
    const translate = useTranslation();
    const { isLoading, data: trademarkList = [], isError } = useGetTrademarks();
    const { selectTrademarkAndRedirect } = useSelectTrademark();
    const [sortState, setSortState] = useState<{ field: string; order: "ascend" | "descend" }>({
        field: "createdAt",
        order: "descend",
    });

    const trademarkListWithResolvedPermissions = useMemo(() => {
        return trademarkList.map((trademark) => ({
            ...trademark,
            resolvedPermissions: new TrademarkPermissions(trademark),
        }));
    }, [trademarkList]);

    const handleRedirectCreateNewTrademark = () => {
        trademarksTracking.onAddTrademarkClick();
        void mnpTrademarkNavigation.trademarksCreate.go();
    };

    const handleSort: ITableProps<TrademarkTableRow>["onSort"] = (sorter) => {
        setSortState({
            field: sorter.columnKey as string,
            order: sorter.order ?? "descend",
        });
    };

    const columnConfig = useMemo(() => getTableColumnConfig(translate, sortState), [translate, sortState]);

    return (
        <ContentSwitcher>
            <Stack
                paddingX="80px"
                paddingY="32px"
                gap="24px"
            >
                <Flex
                    flex="1"
                    justifyContent="flex-end"
                >
                    <Button onClick={handleRedirectCreateNewTrademark}>Add a Trademark</Button>
                </Flex>
                <Table<TrademarkTableRow>
                    theme="carbon"
                    size="large"
                    onRow={(rowData) => {
                        return {
                            onClick: () => {
                                selectTrademarkAndRedirect(rowData);
                            },
                            style: { cursor: "pointer" },
                        };
                    }}
                    noData={getNoData(isError, "trademarks", translate)}
                    isLoading={isLoading}
                    columnConfig={columnConfig}
                    data={trademarkListWithResolvedPermissions}
                    onSort={handleSort}
                />
            </Stack>
        </ContentSwitcher>
    );
};
